/**
 * Utility functions for working with AWS Cognito groups from ID tokens
 */

import { error } from '@/utils/logger';

interface DecodedToken {
  'cognito:groups'?: string[];
  email?: string;
  sub?: string;
  [key: string]: unknown;
}

/**
 * Decode a base64url JWT segment safely.
 */
const decodeJwtPayloadSegment = (segment: string): DecodedToken | null => {
  try {
    // JWT segments are base64url encoded. atob expects base64.
    const base64 = segment
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(segment.length / 4) * 4, '=');
    return JSON.parse(atob(base64));
  } catch (err) {
    error('Error decoding JWT payload segment:', err);
    return null;
  }
};

/**
 * Decodes a JWT ID token and returns the payload
 */
export const decodeIdToken = (idToken: string): DecodedToken | null => {
  try {
    const tokenParts = idToken.split('.');
    if (tokenParts.length !== 3) {
      return null;
    }
    return decodeJwtPayloadSegment(tokenParts[1]);
  } catch (err) {
    error('Error decoding ID token:', err);
    return null;
  }
};

/**
 * Gets Cognito groups from the stored ID token
 */
export const getUserGroups = (): string[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  const idToken = localStorage.getItem('idToken');
  const accessToken = localStorage.getItem('accessToken');

  const groupsFromToken = (token: string | null): string[] => {
    if (!token) return [];
    const decoded = decodeIdToken(token);
    const groups = decoded?.['cognito:groups'];
    return Array.isArray(groups) ? groups : [];
  };

  // Prefer ID token, fallback to access token.
  const idGroups = groupsFromToken(idToken);
  if (idGroups.length > 0) return idGroups;

  return groupsFromToken(accessToken);
};

/**
 * Checks if the user is in a specific group
 */
export const isUserInGroup = (groupName: string): boolean => {
  const target = groupName.trim().toLowerCase();
  const groups = getUserGroups().map((g) => g.trim().toLowerCase());
  return groups.includes(target);
};

/**
 * Checks if the user is an admin (in the 'admin' group)
 */
export const isUserAdmin = (): boolean => {
  return isUserInGroup('admin');
};

/**
 * Gets the current user's email from the ID token
 */
export const getUserEmailFromToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const idToken = localStorage.getItem('idToken');
  if (!idToken) {
    return null;
  }

  const decoded = decodeIdToken(idToken);
  return decoded?.email || null;
};


